import truncate from "lodash/truncate";
import PropTypes from "prop-types";
import React from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Tooltipable from "~/components/effects/tooltipable";
import GlobalContainer from "~/components/global_container";

const TooltipText =
  "If you already have an SPF record in the DNS records for this sending domain, please edit it vs. adding a duplicate one and just add “include:mailgun.org” after “v=spf1” with a space in between.";

const EmailDomainRecords = ({
  domain,
  records,
  receiving = false,
  spfTooltip = false,
}) => {
  const handleCopy = () =>
    GlobalContainer.notify("Copied to Clipboard", "info");
  const getProcessedDomainName = (domainName) => {
    if (domainName === "_dmarc") {
      return domainName;
    }

    const domainParts = domainName.split(".");

    if (domainParts.length === 2) {
      return "@";
    }

    return domainParts.slice(0, -2).join(".");
  };

  const renderStatus = (status) => {
    let className;
    let statusName;

    switch (status) {
      case "valid":
      case true:
        className = "badge badge-outline-green";
        statusName = "Valid";
        break;
      case "unknown":
      case "unverified":
      case false:
        className = "badge badge-outline-warning";
        statusName = "Pending";
        break;
      default:
        className = "badge badge-outline-gray";
        statusName = "Pending";
        break;
    }

    return <span className={className}>{statusName.toUpperCase()}</span>;
  };

  const renderDomainNameWithCopy = (record) => {
    const domainName = getProcessedDomainName(record.name || domain.name);

    return (
      <>
        {domainName}{" "}
        <CopyToClipboard text={domainName} onCopy={handleCopy}>
          <span className="text-green">Copy</span>
        </CopyToClipboard>
      </>
    );
  };

  const renderDomainValueWithCopy = (record) => {
    return (
      <>
        {truncate(record.value)}{" "}
        <CopyToClipboard text={record.value} onCopy={handleCopy}>
          <span className="text-green">Copy</span>
        </CopyToClipboard>
      </>
    );
  };

  if (!records) {
    return null;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover mb0">
        <thead>
          <tr>
            <th className="border-0 text-uppercase" scope="col">
              Type
            </th>
            <th className="border-0 text-uppercase" scope="col">
              Host Name
            </th>
            {receiving && (
              <th className="border-0 text-uppercase" scope="col">
                Priority
              </th>
            )}
            <th className="border-0 text-uppercase" scope="col">
              Required Data
            </th>
            <th className="border-0 text-uppercase" scope="col">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr
              key={`${record.record_type}:${record.name || domain.name}:${record.value}`}
            >
              <td>{record.record_type}</td>
              <td className="text-break">
                {spfTooltip && index === records.length - 1 ? (
                  <Tooltipable placement="bottom" text={TooltipText}>
                    {renderDomainNameWithCopy(record)}
                  </Tooltipable>
                ) : (
                  <>{renderDomainNameWithCopy(record)}</>
                )}
              </td>
              {record.priority && (
                <td className="text-break">
                  Set to any large value not used already. Ex: 50{" "}
                  <CopyToClipboard text={50} onCopy={handleCopy}>
                    <span className="text-green">Copy</span>
                  </CopyToClipboard>
                </td>
              )}
              <td className="text-break">
                {spfTooltip && index === records.length - 1 ? (
                  <Tooltipable placement="bottom" text={TooltipText}>
                    {renderDomainValueWithCopy(record)}
                  </Tooltipable>
                ) : (
                  <>{renderDomainValueWithCopy(record)}</>
                )}
              </td>
              <td>{renderStatus(record.valid)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
EmailDomainRecords.propTypes = {
  domain: PropTypes.object.isRequired,
  records: PropTypes.arrayOf(PropTypes.object),
  receiving: PropTypes.bool,
  spfTooltip: PropTypes.bool,
};

export default EmailDomainRecords;
